/**
 * Top Emission Sources Card
 *
 * Half-width summary card for the Scope 1 / Scope 2 pages. Shows the top N
 * emission sources (flat ranked, no activity-type grouping) and links to the
 * full breakdown on the Insights > Sources dashboard.
 *
 * Data source: reuses `useGetEmissionsBySourcePeriodQuery` with
 * granularity='annual' — we only need `grandTotal` per row, and annual gives
 * us the smallest payload that still produces the correct per-row totals.
 */

import { useMemo, type ElementType } from 'react';
import { useHistory } from 'react-router-dom';
import { ArrowRight, Flame, Inbox, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@g17eco/ui-modern/components/ui/card';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { useGetEmissionsBySourcePeriodQuery } from '../api/emissions';
import { useEmissionsFormatter } from '../hooks/useEmissionsFormatter';
import { formatActivityType } from '../config/activityTypeBranding';
import { resolveSourceLabel } from '../utils/resolveSourceLabel';
import { resolveSourceIcon } from '../utils/resolveSourceIcon';
import { emissionsTrackerRoutes, rootAppPath } from '../routes';
import { generateUrlWithQuery } from '@routes/util';
import type { EmissionScope } from '../types/emissions';

interface TopEmissionSourcesCardProps {
  initiativeId: string;
  periodStart: string;
  periodEnd: string;
  scope: EmissionScope;
  includeChildren?: boolean;
  /** Default 5. */
  limit?: number;
}

export const TopEmissionSourcesCard = ({
  initiativeId,
  periodStart,
  periodEnd,
  scope,
  includeChildren,
  limit = 5,
}: TopEmissionSourcesCardProps) => {
  const { formatCompact } = useEmissionsFormatter();
  const history = useHistory();

  const { data, isLoading } = useGetEmissionsBySourcePeriodQuery(
    {
      initiativeId,
      periodStart,
      periodEnd,
      granularity: 'annual',
      scopes: [scope],
      includeChildren,
    },
    { skip: !initiativeId },
  );

  const topRows = useMemo(() => {
    if (!data?.rows?.length) return [];
    return [...data.rows]
      .filter((r) => r.grandTotal > 0)
      .sort((a, b) => b.grandTotal - a.grandTotal)
      .slice(0, limit);
  }, [data, limit]);

  const viewAllUrl = generateUrlWithQuery(
    emissionsTrackerRoutes.EMISSIONS_INSIGHTS,
    { rootAppPath, initiativeId },
    { tab: 'sources' },
  );

  return (
    <Card className='tw:h-full tw:flex tw:flex-col'>
      <CardHeader>
        <div className='tw:flex tw:justify-between tw:items-center tw:gap-3'>
          <CardTitle className='tw:flex tw:items-center tw:gap-2'>
            <Flame className='tw:size-4 tw:text-muted-foreground' />
            Top Emission Sources
          </CardTitle>
          <Button
            variant='link'
            size='sm'
            onClick={() => history.push(viewAllUrl)}
          >
            View all
            <ArrowRight />
          </Button>
        </div>
      </CardHeader>
      <CardContent className='tw:flex-1'>
        {isLoading ? (
          <div className='tw:flex tw:justify-center tw:py-8'>
            <Loader2 className='tw:size-5 tw:animate-spin tw:text-muted-foreground' />
          </div>
        ) : topRows.length === 0 ? (
          <div className='tw:flex tw:flex-col tw:items-center tw:py-8 tw:text-muted-foreground'>
            <Inbox className='tw:size-8 tw:mb-2 tw:opacity-50' />
            <p className='tw:text-sm tw:mb-0'>No emissions data for this period</p>
          </div>
        ) : (
          <div className='tw:divide-y tw:divide-gray-300'>
            {topRows.map((row) => (
              <SourceRow
                key={`${row.scope}|${row.activityType}|${row.subTypeField ?? ''}|${row.subTypeValue ?? ''}`}
                activityType={row.activityType}
                subTypeField={row.subTypeField}
                subTypeValue={row.subTypeValue}
                grandTotal={row.grandTotal}
                formatCompact={formatCompact}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

interface SourceRowProps {
  activityType: string;
  subTypeField: 'fuelType' | 'vehicleType' | 'refrigerantType' | 'processType' | null;
  subTypeValue: string | null;
  grandTotal: number;
  formatCompact: ReturnType<typeof useEmissionsFormatter>['formatCompact'];
}

const SourceRow = ({
  activityType,
  subTypeField,
  subTypeValue,
  grandTotal,
  formatCompact,
}: SourceRowProps) => {
  const { sourceLabel } = resolveSourceLabel(activityType, subTypeField, subTypeValue);
  const icon = resolveSourceIcon(subTypeField, subTypeValue);
  const groupLabel = formatActivityType(activityType);

  // When there's no sub-type (e.g. Purchased Electricity), the primary label
  // *is* the group label — don't duplicate it as a secondary.
  const primaryLabel = sourceLabel ?? groupLabel;
  const secondaryLabel = sourceLabel ? groupLabel : null;

  const co2e = formatCompact(grandTotal);

  return (
    <div className='tw:flex tw:justify-between tw:items-center tw:gap-3 tw:py-3 first:tw:pt-0 last:tw:pb-0' style={{ height: '4rem' }}>
      <div className='tw:flex tw:items-center tw:gap-3 tw:min-w-0'>
        {icon ? (
          <SourceIcon Icon={icon.Icon} iconBg={icon.iconBg} iconColor={icon.iconColor} />
        ) : (
          <SourceIcon Icon={Flame} iconBg='tw:bg-orange-100' iconColor='tw:text-orange-600' />
        )}
        <div className='tw:min-w-0'>
          <div className='tw:text-sm tw:font-medium tw:truncate'>{primaryLabel}</div>
          {secondaryLabel ? (
            <div className='tw:text-xs tw:text-muted-foreground tw:truncate'>{secondaryLabel}</div>
          ) : null}
        </div>
      </div>
      <div className='tw:text-sm tw:font-medium tw:tabular-nums tw:shrink-0'>
        {co2e.formatted}
        <span className='tw:text-xs tw:text-muted-foreground tw:font-normal tw:ml-1'>
          {co2e.unit}
        </span>
      </div>
    </div>
  );
};

const SourceIcon = ({
  Icon,
  iconBg,
  iconColor,
}: {
  Icon: ElementType;
  iconBg: string;
  iconColor: string;
}) => (
  <div
    className={cn(
      'tw:rounded-lg tw:flex tw:items-center tw:justify-center tw:size-9 tw:shrink-0',
      iconBg,
    )}
  >
    <Icon className={cn('tw:size-4', iconColor)} />
  </div>
);
